import { array } from "fp-ts";
import { pipe } from "fp-ts/lib/function";
import { TRoadblockConfiguration } from "../roadblockTypesAndCodecs";
import { CanAccessPortal, MustAgreeToTOE, OwnershipStatus_DisplayName, RequiredToHaveOwnershipStatus, RequiresDualRepConsent, RequiresIdCheck, UserCaseUserType_UserCaseUserTypeDisplayName } from "../../codecs/UserCaseUserType";
import { RequiresVideoCallVerification } from "../../codecs/UserCaseUserType";
import { DateTime } from "luxon";
import { anyActiveLedgerEntryOfType } from "./SharedExchangeAndPreCompletionItems";
import { clientName, clientsOfTypes, fact, formatEvidenceDate, neutralFact } from "../evidence/evidenceHelpers";
import { TCasesPurchasePaymentMethod1 } from "../../codecs/CasesPurchasePaymentMethod";
import { TRoadblockEvidenceLink } from "../../codecs/form/RoadblockForm";

const clientsLink: TRoadblockEvidenceLink = { block: "case_details", section: "clients" };
const callsLink: TRoadblockEvidenceLink = { block: "case_details", section: "video_verification_calls" };
const dualRepLink: TRoadblockEvidenceLink = { block: "case_details", section: "dual_rep" };
const clientFundsLink: TRoadblockEvidenceLink = { block: "case_details", section: "client_funds" };

const latestValidCheckExpiry = (
    checks: Array<{ expires_at: string }> | undefined,
    c: { has_completed: string | null; completion_deadline: string | null; expected_completion_date: string | null },
): string | null => pipe(
    checks || [],
    array.filter((check) => isIdCheckOrVideoCallValidForCase({ ...check, status: "pass" }, c) === "pass"),
    array.map((check) => check.expires_at),
    (expiries) => expiries.length === 0 ? null : expiries.reduce((latest, e) => (e > latest ? e : latest)),
);

const purchasePaymentMethodDisplay = (method: TCasesPurchasePaymentMethod1): string =>
    method === "unknown" ? "Not set"
        : method === "cash" ? "Cash"
            : method === "mortgage" ? "Mortgage"
                : "No payment";

const UserTypesToIDCheckList = pipe(
    RequiresIdCheck,
    array.map((type) => `* ${UserCaseUserType_UserCaseUserTypeDisplayName(type)}`),
    (a) => a.join(`\n`),
);

const UserTypesToVideoCallList = pipe(
    RequiresVideoCallVerification,
    array.map((type) => `* ${UserCaseUserType_UserCaseUserTypeDisplayName(type)}`),
    (a) => a.join(`\n`),
);

const UserTypesWithPortalAccessList = pipe(
    CanAccessPortal,
    array.map((type) => `* ${UserCaseUserType_UserCaseUserTypeDisplayName(type)}`),
    (a) => a.join(`\n`),
);

const UserTypesMustAgreeToTOEList = pipe(
    MustAgreeToTOE,
    array.map((type) => `* ${UserCaseUserType_UserCaseUserTypeDisplayName(type)}`),
    (a) => a.join(`\n`),
);

type TCheckValidForCaseStatus =
    "check_has_failed_status"
    | "expires_before_completion_date"
    | "completion_deadline_and_expected_completion_in_past_and_check_currently_expired"
    | "completion_deadline_and_expected_completion_are_in_future_but_id_check_expires_before_one_of_them"
    | "expires_before_completion_deadline"
    | "expires_before_expected_completion_date"
    | "check_is_currently_expired"
    | "pass"
    ;

/**
 * There's some quite fiddly conditions here around ID checks/video calls and checking if they should be expired.
 * The complexity comes from the fact that we need to check if the ID checks/video calls are valid *specifically for the case in question*
 * E.g. if the case is completed, an expired ID check is fine as long as it didn't expire before completion.
 * The rules are the same for both ID checks and video calls and are as follows:
 *  * if it expires after the completion date, it passes
 *  * if no completion date and either completion deadline or expected completion date are set, then check expiry should be further in the future than the *latest* of either the completion deadline OR the expected completion date
 *  * if none of the above dates set, it passes as long as it is not *currently* expired
 *  * if no completion date and completion deadline OR the expected completion date are in the past, check must be currently valid for it to pass (the dates in the past must be wrong in this instance)
 * 
 */
export const isIdCheckOrVideoCallValidForCase = (
    check: {
        expires_at: string;
        status: "pass" | "fail" // this prop is to be mapped on to from whatever pass/fail model is applicable
    },
    c: {
        has_completed: string | null,
        completion_deadline: string | null,
        expected_completion_date: string | null
    }
): TCheckValidForCaseStatus => {
    // If the check itself fails, just fail immediately
    if (check.status === "fail") {
        return "check_has_failed_status";
    }

    // If it expires after the has_completed date, it passes
    if (
        c.has_completed
        && check.expires_at < c.has_completed
    ) {
        return "expires_before_completion_date";
    }

    const currentTimestamp = DateTime.local().toISO();

    // If both completion_deadline and expected_completion_date are in past and check is not currently valid, then fail
    if (
        !c.has_completed
        && c.completion_deadline && c.expected_completion_date
        && c.completion_deadline < currentTimestamp
        && c.expected_completion_date < currentTimestamp
        && check.expires_at < currentTimestamp
    ) {
        return "completion_deadline_and_expected_completion_in_past_and_check_currently_expired";
    }

    // If both completion_deadline and expected_completion_date set, expirey must be later than both
    if (
        !c.has_completed
        && c.completion_deadline && c.expected_completion_date
        && (
            check.expires_at < c.completion_deadline
            || check.expires_at < c.expected_completion_date
        )
    ) {
        return "completion_deadline_and_expected_completion_are_in_future_but_id_check_expires_before_one_of_them";
    }

    // If only completion_deadline set, expirey must be later than it
    if (
        !c.has_completed
        && c.completion_deadline && !c.expected_completion_date
        && (check.expires_at < c.completion_deadline)
    ) {
        return "expires_before_completion_deadline";
    }

    // If only expected_completion_date set, expirey must be later than it
    if (
        !c.has_completed
        && !c.completion_deadline && c.expected_completion_date
        && (check.expires_at < c.expected_completion_date)
    ) {
        return "expires_before_expected_completion_date";
    }

    if (
        !c.has_completed
        && !c.completion_deadline
        && !c.expected_completion_date
        && check.expires_at < currentTimestamp) {
        return "check_is_currently_expired"
    }

    return "pass";
}

const clientPassesCheck = (
    checks: Array<{
        expires_at: string;
    }> | undefined,
    c: {
        has_completed: string | null,
        completion_deadline: string | null,
        expected_completion_date: string | null
    }
): boolean => pipe(
    checks || [],
    (checksArray) =>
        checksArray.length
            ? pipe(
                checksArray,
                array.reduce(false, (acc, check) => {
                    return isIdCheckOrVideoCallValidForCase({ ...check, status: "pass" }, c) === "pass" ? true : acc;
                })
            )
            : false
);

export const ClientVerification: TRoadblockConfiguration = {
    type: "client_verification",
    display_name: "Client Verification",
    only_create_if_resolved: [],
    requires_authoriser_signoff: false,
    blocks_exchange: true,
    create: {
        create_condition_description: "Instructed cases that have not yet completed",
        predicate: (c) => c.case.is_instructed !== null && c.case.has_completed === null
    },
    checklist: [
        {
            type: "client_id_checks_passed",
            is_resolved_by_short_description: "Client ID checks passed",
            create: {
                create_condition_description: "All cases",
                predicate: () => true
            },
            resolve: {
                is_resolved_by_long_description: `Having a valid ID check for all clients who are one of the following:
                ${UserTypesToIDCheckList}.
                If the case has a company executor, no other ID checks are needed as those checks are handled by another process.
                Note: ID checks are valid for 12 months and must cover until the expected completion or completion deadline if set.`,
                predicate: (c) =>
                    pipe(
                        c.clients,
                        array.reduce("no_id_checks_or_co_execs", (acc: "pass" | "at_least_one_failed_id" | "no_id_checks_or_co_execs", client) => {
                            const clientRequiresIdCheck = RequiresIdCheck.includes(client.user_type);

                            const clientPassesIdCheck = clientPassesCheck(c.clients_passed_id_checks[client.id], c.case);

                            return (clientRequiresIdCheck && !clientPassesIdCheck)
                                ? "at_least_one_failed_id"
                                : (clientRequiresIdCheck && clientPassesIdCheck && acc !== "at_least_one_failed_id")
                                    ? "pass"
                                    : (client.user_type === "company_executor" && acc !== "at_least_one_failed_id")
                                        ? "pass"
                                        : acc;
                        }
                        ),
                        (acc) => acc === "pass" ? true : false,
                    ),
                evidence: (c) => clientsOfTypes(c.clients, RequiresIdCheck).map((client) => {
                    const expiry = latestValidCheckExpiry(c.clients_passed_id_checks[client.id], c.case);
                    return fact(
                        clientName(client),
                        expiry !== null ? `Valid to ${formatEvidenceDate(expiry)}` : "No valid ID check",
                        expiry !== null,
                        clientsLink,
                    );
                }),
            }
        },
        {
            type: "client_video_calls_passed",
            is_resolved_by_short_description: "All clients passed initial verification call",
            create: {
                create_condition_description: "All cases",
                predicate: () => true
            },
            resolve: {
                is_resolved_by_long_description: `Having a passed video call with all clients who are one of the following:
                ${UserTypesToVideoCallList}
                Note: Video calls are valid for 12 months and must cover until the expected completion or completion deadline if set.`,
                predicate: (c) =>
                    pipe(
                        c.clients,
                        array.reduce("no_vid_calls_or_co_execs", (acc: "pass" | "at_least_one_client_without_vid_call" | "no_vid_calls_or_co_execs", client) => {
                            const clientRequiresVidCall = RequiresVideoCallVerification.includes(client.user_type);

                            const clientPassesVidCall = clientPassesCheck(c.clients_passed_video_calls[client.id], c.case);

                            return (clientRequiresVidCall && !clientPassesVidCall)
                                ? "at_least_one_client_without_vid_call"
                                : (clientRequiresVidCall && clientPassesVidCall && acc !== "at_least_one_client_without_vid_call")
                                    ? "pass"
                                    : (client.user_type === "company_executor" && acc !== "at_least_one_client_without_vid_call")
                                        ? "pass"
                                        : acc;
                        }
                        ),
                        (acc) => acc === "pass" ? true : false,
                    ),
                evidence: (c) => clientsOfTypes(c.clients, RequiresVideoCallVerification).map((client) => {
                    const expiry = latestValidCheckExpiry(c.clients_passed_video_calls[client.id], c.case);
                    return fact(
                        clientName(client),
                        expiry !== null ? `Valid to ${formatEvidenceDate(expiry)}` : "No passed call",
                        expiry !== null,
                        callsLink,
                    );
                }),
            }
        },
        {
            type: "client_no_vulnerability_concerns_on_call",
            is_resolved_by_short_description: "All clients had no vulnerability concerns on call",
            create: {
                create_condition_description: "All cases",
                predicate: () => true
            },
            resolve: {
                is_resolved_by_long_description: `All clients who passed video calls must have no vulnerability concerns recorded. Each passed video call participant must have vulnerability_concerns set to "none".`,
                predicate: (c) =>
                    pipe(
                        c.clients,
                        array.reduce({ anyClientRequiresVidCall: false, hasCheckedAtLeastOne: false, allClear: true }, (acc, client) => {
                            const clientRequiresVidCall = RequiresVideoCallVerification.includes(client.user_type);
                            if (!clientRequiresVidCall) {
                                return acc;
                            }
                            const clientVideoCalls = c.clients_passed_video_calls[client.id] || [];
                            // If there are no passed video calls for this client, don't fail on this checklist item
                            // (the video calls passed checklist item handles that)
                            if (clientVideoCalls.length === 0) {
                                return { ...acc, anyClientRequiresVidCall: true };
                            }
                            // All passed video calls for this client must have vulnerability_concerns === "none"
                            const allCallsHaveNoConcerns = pipe(
                                clientVideoCalls,
                                array.reduce(true, (innerAcc, call) =>
                                    call.vulnerability_concerns !== "none" ? false : innerAcc
                                )
                            );
                            return {
                                anyClientRequiresVidCall: true,
                                hasCheckedAtLeastOne: true,
                                allClear: allCallsHaveNoConcerns ? acc.allClear : false,
                            };
                        }),
                        (acc) =>
                            // If no clients require video calls (e.g. company executor only), resolve as passed
                            !acc.anyClientRequiresVidCall
                                ? true
                                // If clients require calls and at least one has been checked, all must be clear
                                : acc.hasCheckedAtLeastOne && acc.allClear,
                    ),
                evidence: (c) => clientsOfTypes(c.clients, RequiresVideoCallVerification).map((client) => {
                    const calls = c.clients_passed_video_calls[client.id] || [];
                    if (calls.length === 0) {
                        return neutralFact(clientName(client), "No call yet", callsLink);
                    }
                    const concern = calls.reduce(
                        (worst, call) => call.vulnerability_concerns !== "none" ? call.vulnerability_concerns : worst,
                        "none",
                    );
                    return fact(clientName(client), concern === "none" ? "No concerns" : concern, concern === "none", callsLink);
                }),
            }
        },
        {
            type: "email_phone_validation_check_completed",
            is_resolved_by_short_description: "Client emails and phone validated",
            create: {
                create_condition_description: "All cases",
                predicate: () => true
            },
            resolve: {
                is_resolved_by_long_description: `Every client who can access the portal must have a verified email address and at least one verified phone number, or an authorised exemption recorded by an authoriser/MLRO. Verification is recorded against the user — \`users.email_is_verified\` and \`users_phone_numbers.is_verified\`; exemptions against \`users__cases.no_digital_contact_channel_approved_at\`. Resolves automatically when every portal-eligible client is contact-validated or exempt.

                Portal-eligible client types:
                ${UserTypesWithPortalAccessList}.

                Excluded by construction (no personal email/phone to validate): Deceased, Introducer, Company executor.`,
                predicate: (c) => pipe(
                    c.clients,
                    array.reduce(true, (acc, client) =>
                        CanAccessPortal.includes(client.user_type)
                            && client.no_digital_contact_channel_approved_at == null
                            && (!client.email_is_verified || !client.has_verified_phone_number)
                            ? false
                            : acc
                    ),
                ),
                evidence: (c) => clientsOfTypes(c.clients, CanAccessPortal).flatMap((client) =>
                    client.no_digital_contact_channel_approved_at != null
                        ? [
                            fact(`${clientName(client)} — digital contact`, "Exemption approved", true, clientsLink),
                        ]
                        : [
                            fact(`${clientName(client)} — email`, client.email_is_verified ? "Verified" : "Not verified", client.email_is_verified, clientsLink),
                            fact(`${clientName(client)} — phone`, client.has_verified_phone_number ? "Verified" : "Not verified", client.has_verified_phone_number, clientsLink),
                        ]
                ),
            }
        },
        {
            type: "terms_of_engagement_agreed",
            is_resolved_by_short_description: "Clients agreed to terms of engagement",
            create: {
                create_condition_description: "Cases that do not have a company executor",
                predicate: (c) => pipe(
                    c.clients,
                    array.reduce(true, (acc, client) => client.user_type === "company_executor" ? false : acc)
                )
            },
            resolve: {
                is_resolved_by_long_description: `Having at least one of the clients that are of the following type agree to the terms and conditions in the portal:
                ${UserTypesMustAgreeToTOEList}.`,
                predicate: (c) => pipe(
                    c.clients,
                    array.reduce(false, (acc, client) =>
                        client.agreed_to_toe_at !== null
                            && MustAgreeToTOE.includes(client.user_type)
                            ? true
                            : acc
                    ),
                ),
                evidence: (c) => clientsOfTypes(c.clients, MustAgreeToTOE).map((client) =>
                    fact(
                        clientName(client),
                        client.agreed_to_toe_at !== null ? `Agreed ${formatEvidenceDate(client.agreed_to_toe_at)}` : "Not agreed",
                        client.agreed_to_toe_at !== null,
                        clientsLink,
                    ),
                ),
            }
        },
        {
            type: "consent_to_dual_rep",
            is_resolved_by_short_description: "All clients consented to dual rep",
            create: {
                create_condition_description: "Dual rep case",
                predicate: (c) => c.dual_rep.case_is_dual_rep
            },
            resolve: {
                is_resolved_by_long_description: `Having all clients consent to dual rep`,
                predicate: (c) => pipe(
                    c.clients,
                    array.reduce(true, (acc, client) =>
                        RequiresDualRepConsent.includes(client.user_type) && !c.dual_rep.client_ids_with_dual_rep_consent.includes(client.id)
                            ? false
                            : acc
                    )
                ),
                evidence: (c) => clientsOfTypes(c.clients, RequiresDualRepConsent).map((client) => {
                    const consented = c.dual_rep.client_ids_with_dual_rep_consent.includes(client.id);
                    return fact(clientName(client), consented ? "Consented" : "Not consented", consented, dualRepLink);
                }),
            }
        },
        {
            type: "purchase_payment_method_set",
            is_resolved_by_short_description: "Client set purchase payment method",
            create: {
                create_condition_description: "Purchase cases",
                predicate: (c) => c.case.transaction_type === "purchase" ? true : false
            },
            resolve: {
                is_resolved_by_long_description: `Setting a purchase payment method`,
                predicate: (c) => c.case.purchase_payment_method !== "unknown" ? true : false,
                evidence: (c) => [
                    fact(
                        "Payment method",
                        purchasePaymentMethodDisplay(c.case.purchase_payment_method),
                        c.case.purchase_payment_method !== "unknown",
                    ),
                ],
            }
        },
        {
            type: "potential_fraud_reviewed",
            is_resolved_by_short_description: "Potential-fraud flag reviewed",
            create: {
                create_condition_description: "At least one client's latest ID check is flagged as potential fraud",
                predicate: (c) => pipe(
                    c.clients,
                    array.reduce(false, (acc, client) =>
                        client.latest_id_check_potential_fraud ? true : acc
                    ),
                )
            },
            resolve: {
                is_resolved_by_long_description: `Reviewing the potential-fraud flag on the client's ID check and recording an outcome of either "not fraud" or "is fraud". An outcome of "is fraud" resolves this item but leaves the client without a valid ID check, so Client ID checks passed stays open.`,
                predicate: (c) => pipe(
                    c.clients,
                    array.reduce(true, (acc, client) =>
                        client.latest_id_check_potential_fraud
                            && client.latest_id_check_potential_fraud_outcome === "unknown"
                            ? false
                            : acc
                    ),
                ),
                evidence: (c) => pipe(
                    c.clients,
                    array.filter((client) => client.latest_id_check_potential_fraud),
                    array.map((client) => {
                        const outcome = client.latest_id_check_potential_fraud_outcome;
                        return outcome === "unknown"
                            ? neutralFact(clientName(client), "Flagged — awaiting review", clientsLink)
                            : fact(
                                clientName(client),
                                outcome === "not_fraud" ? "Reviewed — not fraud" : "Reviewed — confirmed fraud",
                                outcome === "not_fraud",
                                clientsLink,
                            );
                    }),
                ),
            }
        },
        {
            type: "failed_video_calls_reviewed",
            is_resolved_by_short_description: "Failed verification call reviewed by an authoriser",
            create: {
                create_condition_description: "At least one client has a failed verification call that hasn't expired",
                predicate: (c) => pipe(
                    c.clients,
                    array.reduce(false, (acc, client) =>
                        (c.clients_failed_video_calls[client.id] || []).length > 0 ? true : acc
                    ),
                )
            },
            resolve: {
                is_resolved_by_long_description: `Every client with a failed verification call either passing a later call, or the failed outcome expiring. A failure needs an authoriser to review it rather than simply re-booking the call.`,
                predicate: (c) => pipe(
                    c.clients,
                    array.reduce(true, (acc, client) =>
                        (c.clients_failed_video_calls[client.id] || []).length > 0
                            && !clientPassesCheck(c.clients_passed_video_calls[client.id], c.case)
                            ? false
                            : acc
                    ),
                ),
                evidence: (c) => pipe(
                    c.clients,
                    array.filter((client) => (c.clients_failed_video_calls[client.id] || []).length > 0),
                    array.map((client) => {
                        const nowPasses = clientPassesCheck(c.clients_passed_video_calls[client.id], c.case);
                        return fact(
                            clientName(client),
                            nowPasses ? "Failed call, since passed" : "Failed call — needs authoriser review",
                            nowPasses,
                            callsLink,
                        );
                    }),
                ),
            }
        },
        {
            type: "conflicts_of_interest_reviewed",
            is_resolved_by_short_description: "Conflicts of interest reviewed",
            create: {
                create_condition_description: "Cases with at least one conflict of interest raised",
                predicate: (c) => c.conflicts_of_interest.length > 0,
            },
            resolve: {
                is_resolved_by_long_description: `Every conflict of interest on the case having been reviewed and moved off "flagged" — to no conflict, conflict but good to continue, or conflict and unable to continue.`,
                predicate: (c) => c.conflicts_of_interest.every((coi) => coi.status !== "flagged"),
                evidence: (c) => c.conflicts_of_interest.map((coi, index) =>
                    fact(
                        `Conflict ${index + 1}`,
                        coi.status === "flagged" ? "Flagged — needs review"
                            : coi.status === "resolved_no_conflict" ? "Reviewed — no conflict"
                                : coi.status === "resolved_conflict_but_good_to_conitnue" ? "Reviewed — good to continue"
                                    : "Reviewed — unable to continue",
                        coi.status !== "flagged",
                        clientsLink,
                    )
                ),
            }
        },
        {
            type: "peps_resolved",
            is_resolved_by_short_description: "PEPs/sanctions hit — verified & passed",
            create: {
                create_condition_description: "At least one client's latest ID check returned PEPs/sanctions = requires_investigation",
                predicate: (c) => pipe(
                    c.clients,
                    array.reduce(false, (acc, client) =>
                        client.latest_id_check_peps_sanctions_status === "requires_investigation" ? true : acc
                    ),
                )
            },
            resolve: {
                is_resolved_by_long_description: `Reviewing the PEPs/sanctions match and setting the client's ID check PEPs/sanctions status to Pass. The client's signed PEP declaration is evidence, but a staff member always confirms the pass.`,
                predicate: (c) => pipe(
                    c.clients,
                    array.reduce(true, (acc, client) =>
                        client.latest_id_check_peps_sanctions_status === "requires_investigation" ? false : acc
                    ),
                ),
                evidence: (c) => pipe(
                    c.clients,
                    array.filter((client) => client.latest_id_check_peps_sanctions_status === "requires_investigation"),
                    array.map((client) => {
                        const signedAt = client.pep_declaration_signed_at;
                        const hitLabel = client.latest_id_check_has_sanction_match ? "Sanction + PEP hit" : "PEP hit";
                        const value = signedAt !== null
                            ? `${hitLabel} — client declared not them ${formatEvidenceDate(signedAt)}; verify & set Pass`
                            : `${hitLabel} — awaiting client declaration`;
                        return neutralFact(clientName(client), value, clientsLink);
                    }),
                ),
            }
        },
        {
            type: "peps_holp_signoff",
            is_resolved_by_short_description: "Report PEP to HOLP — get sign-off to proceed",
            create: {
                create_condition_description: "At least one client confirmed they are the PEP/sanctions match (latest ID check PEPs/sanctions = fail)",
                predicate: (c) => pipe(
                    c.clients,
                    array.reduce(false, (acc, client) =>
                        client.latest_id_check_peps_sanctions_status === "fail" ? true : acc
                    ),
                )
            },
            resolve: {
                is_resolved_by_long_description: `Reporting the confirmed PEP to the HOLP and getting sign-off. Once decided, set the client's ID check PEPs/sanctions status off Fail (to Pass if cleared to proceed; otherwise the case is disinstructed).`,
                predicate: (c) => pipe(
                    c.clients,
                    array.reduce(true, (acc, client) =>
                        client.latest_id_check_peps_sanctions_status === "fail" ? false : acc
                    ),
                ),
                evidence: (c) => pipe(
                    c.clients,
                    array.filter((client) => client.latest_id_check_peps_sanctions_status === "fail"),
                    array.map((client) => neutralFact(clientName(client), "Confirmed PEP — report to HOLP for sign-off", clientsLink)),
                ),
            }
        },
        {
            type: "money_on_account_received",
            is_resolved_by_short_description: "Money on account received",
            create: {
                create_condition_description: "Cases that have an active 'Money from client (initial payment)' ledger entry.",
                predicate: (c) => anyActiveLedgerEntryOfType(c, "money_from_client_initial_payment"),
            },
            resolve: {
                is_resolved_by_long_description: `Marking the 'Money from client (initial payment)' ledger entry as paid.`,
                predicate: (c) =>
                    c.case_ledger_entries.some(
                        (l) =>
                            l.type === "money_from_client_initial_payment"
                            && l.deleted_at === null
                            && l.paid_at !== null
                    ),
                evidence: (c) => {
                    const entry = c.case_ledger_entries.find(
                        (l) => l.type === "money_from_client_initial_payment" && l.deleted_at === null,
                    );
                    return [
                        fact(
                            "Initial payment",
                            entry?.paid_at ? `Received ${formatEvidenceDate(entry.paid_at)}` : "Not received",
                            entry?.paid_at != null,
                            clientFundsLink,
                        ),
                    ];
                },
            }
        },
        {
            type: "transfer_ownership_status_set_for_all_clients",
            is_resolved_by_short_description: "Ownership status set for every transfer client",
            create: {
                create_condition_description: "Transfer-of-equity cases",
                predicate: (c) => c.case.transaction_type === "transfer"
            },
            resolve: {
                is_resolved_by_long_description: `Every party whose user type can be on the title (client, registered third party, executor, deceased, personal representative, LPA) must have their ownership status set to something other than "unknown".`,
                predicate: (c) => pipe(
                    c.clients,
                    array.reduce(true, (acc, client) =>
                        RequiredToHaveOwnershipStatus.includes(client.user_type)
                            && client.ownership_status === "unknown"
                            ? false
                            : acc
                    ),
                ),
                evidence: (c) => clientsOfTypes(c.clients, RequiredToHaveOwnershipStatus).map((client) =>
                    fact(
                        clientName(client),
                        client.ownership_status === "unknown" ? "Not set" : OwnershipStatus_DisplayName(client.ownership_status),
                        client.ownership_status !== "unknown",
                        clientsLink,
                    ),
                ),
            }
        }]
};
